import { useEffect, useState } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";

import { Store as StoreIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, useSnackbar } from "@/features/ui";
import {
  platformLabels,
  ResetPasswordDialog,
  UserInformationDialog,
} from "@/features/users";

export const User = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try {
        const {
          data: { data },
        } = await apiClient.get(`users/${userId}`);
        setUser(data);
      } catch (error) {
        showSnackbar(
          getErrorMessage(error, "Gagal memuat pengguna")
        );
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [showSnackbar, userId]);

  const handlePasswordReset = async () => {
    try {
      setResetting(true);
      const {
        data: { data },
      } = await apiClient.post(`users/${userId}/reset-password`);
      setResetPasswordUser(data);
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Gagal memuat pengguna")
      );
    } finally {
      setResetting(false);
    }
  };

  const toolbarContent = hasPermission(PermissionEnum.READ_USER_OUTLET) && (
        <Box>
          <IconButton
            LinkComponent={Link}
            to="outlets"
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            <StoreIcon />
          </IconButton>
        </Box>
      );

  return loading ? (
    <CenteredLoading sx={{ height: "80vh" }} />
  ) : (
    <PageContainer
      maxWidth="xl"
      title="Pengguna"
      breadcrumbs={[
        { title: "Pengguna", path: "/setting/users" },
        { title: user.name },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={3}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body1">Informasi Pengguna</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Typography variant="caption" color="gray">
                Nama
              </Typography>
              <Typography variant="body2">{user.name}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Typography variant="caption" color="gray">
                Username
              </Typography>
              <Typography variant="body2">{user.username}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Typography variant="caption" color="gray">
                Email
              </Typography>
              <Typography variant="body2">{user.email}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Typography variant="caption" color="gray">
                Nomor Kontak
              </Typography>
              <Typography variant="body2">
                {user.contactNumber ?? "-"}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Typography variant="caption" color="gray">
                Role
              </Typography>
              <Typography variant="body2">{user.role.name}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Typography variant="caption" color="gray">
                Platform
              </Typography>
              <Typography variant="body2">
                {platformLabels[user.platform]}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        {hasPermission(PermissionEnum.UPDATE_USERS) && (
          <Box
            sx={{
              gap: 3,
              display: "flex",
              justifyContent: "flex-end"
            }}>
            <Button
              variant="contained"
              color="error"
              loading={resetting}
              onClick={() => setResetPasswordDialogOpen(true)}
            >
              Reset Password
            </Button>
            <Button variant="contained" component={Link} to="edit">
              Ubah Pengguna
            </Button>
          </Box>
        )}
        {resetPasswordDialogOpen && (
          <ResetPasswordDialog
            open
            onClose={() => setResetPasswordDialogOpen(false)}
            onSubmit={handlePasswordReset}
          />
        )}
        {resetPasswordUser && (
          <UserInformationDialog
            open
            user={{ ...user, ...resetPasswordUser }}
            title="Reset Password berhasil"
            onClose={() => {
              setResetPasswordUser(null);
              navigate(-1);
            }}
          />
        )}
      </Stack>
    </PageContainer>
  );
};
